// src/components/session/StepToolbar.js
// One slim line above every step, the same on all steps: the step's status (steps the user
// confirms), the fill-in helpers (assistant: steps 1-4, chatbot copy-paste: steps 1-3) and the key terms.
// RoleMarker (who acts at a step) is shown under each step name in the stepper.
import React from "react";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import { ROLE, STEP_ROLES } from "../../content/steps";
import { StepStatusChip } from "../common/StepStatusBanner";
import GlossaryTerm from "../common/GlossaryTerm";

export function RoleMarker({ step }) {
  const role = ROLE[STEP_ROLES[step]];
  if (!role) return null;
  return (
    <Tooltip title={role.label}>
      <Box
        component="span"
        role="img"
        aria-label={role.label}
        sx={{ fontSize: "0.95rem", lineHeight: 1, opacity: 0.8, whiteSpace: "nowrap", cursor: "default" }}
      >
        {role.emoji}
      </Box>
    </Tooltip>
  );
}

export default function StepToolbar({ step, status, onChatbot, onAssistant }) {
  const canPrepare = step >= 1 && step <= 4; // the AI assistant (MCP) helps with steps 1-4
  const canChatbot = step >= 1 && step <= 3; // step 4 via chatbot copy-paste is not offered (MLUs stay in the tool)
  // Only on steps the user confirms; "proposed" is left out (the assistant banner already says it)
  const showStatus = !!status && status !== "proposed" && [1, 2, 3, 4, 6].includes(step);
  return (
    <Box>
      {(canPrepare || showStatus) && (
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {showStatus && <StepStatusChip status={status} />}
        <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />
        {canPrepare && (
          <>
            {canChatbot && <Button size="small" variant="outlined" startIcon={<ContentPasteGoIcon />} onClick={onChatbot} sx={{ textTransform: "none" }}>
              Prepare with any AI chatbot
            </Button>}
            <Button size="small" variant="outlined" startIcon={<SmartToyOutlinedIcon />} onClick={onAssistant} sx={{ textTransform: "none" }}>
              Let an AI assistant help
            </Button>
          </>
        )}
      </Stack>
      )}
      {step >= 4 && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          Key terms:{" "}
          <GlossaryTerm id="mlu">MLU</GlossaryTerm> · <GlossaryTerm id="framework">Framework</GlossaryTerm> ·{" "}
          <GlossaryTerm id="level">Level</GlossaryTerm> · <GlossaryTerm id="tripleLoop">Triple-loop learning</GlossaryTerm> ·{" "}
          <GlossaryTerm id="coCreation">Co-creation</GlossaryTerm>
        </Typography>
      )}
    </Box>
  );
}
